// app/javascript/controllers/calendar_actions_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "day", "overlay", "backdrop", "sheet", "dateText",
    "existingWrap",
    "rtpNewBtn", "phaseNewBtn", "phaseOtherBtn", "irNewBtn", "irFieldBtn",
    "irClinicalBtn", "missedDayWrap", "missedDateInput", "missedDayForm"
  ]

  static values = {
    date: String,
    athleteId: Number,
    athleteProfileId: Number,
    affiliateId: Number,
    rtpId: Number,
    irNewPath: String,     // /affiliates/:affiliate_id/typed_incident_reports/new
    phaseNewPath: String,  // /rtps/:rtp_id/rtp_phases/new
    missedDayPath: String,
    rtpShowPath: String,
    rtpNewPath: String,
    showRtpNew: Boolean,
    latestIrId: Number,
    showNewIrButton: Boolean,
    showNewRtpButton: Boolean,
    showNewRtpPhaseButton: Boolean,
    showMissedDaysButton: Boolean
  }

  connect() {
    // Close actions
    this.overlayTarget.querySelector("[data-role='close']")?.addEventListener("click", () => this.close())
    this.backdropTarget?.addEventListener("click", () => this.close())

    // Repositioning + ESC
    this._reposition = () => { if (this.currentCell) this.positionSheetOver(this.currentCell) }
    window.addEventListener("resize", this._reposition, { passive: true })
    window.addEventListener("scroll", this._reposition, true)
    this._esc = (e) => { if (e.key === "Escape") this.close() }
    document.addEventListener("keydown", this._esc)

    // Missed-day buttons (delegated)
    this.overlayTarget.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-role='missed-day']")
      if (!btn) return
      e.preventDefault()
      e.stopPropagation()
      this.saveMissedDay(this.currentDate, btn.dataset.value)
    })

    // Wire “New …” buttons once; URLs computed on click using currentDate
    if (this.hasIrNewBtnTarget) {
      this.irNewBtnTarget.type = "button"
      this.irNewBtnTarget.addEventListener("click", (ev) => {
        ev.preventDefault(); ev.stopPropagation()
        const url = this.irNewUrl(this.currentDate)
        if (url) this.navigateTo(url)
      })
    }

    if (this.hasPhaseNewBtnTarget) {
      this.phaseNewBtnTarget.type = "button"
      this.phaseNewBtnTarget.addEventListener("click", (ev) => {
        ev.preventDefault(); ev.stopPropagation()
        const url = this.phaseNewUrl(this.currentDate)
        if (url) this.navigateTo(url)
      })

      this.phaseOtherBtnTarget.type = "button"
      this.phaseOtherBtnTarget.addEventListener("click", (ev) => {
        ev.preventDefault(); ev.stopPropagation()
        const url = this.showRtpUrl()
        if (url) this.navigateTo(url)
      })
    }

    if (this.hasRtpNewBtnTarget) {
      this.rtpNewBtnTarget.type = "button"
      this.rtpNewBtnTarget.addEventListener("click", (ev) => {
        ev.preventDefault(); ev.stopPropagation()
        const url = this.rtpNewUrl(this.currentDate)
        if (url) this.navigateTo(url)
      })
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this._esc)
    window.removeEventListener("resize", this._reposition)
    window.removeEventListener("scroll", this._reposition, true)
  }

  open(e) {
    // If the click is on (or inside) a real link, let it behave normally.
    const link = e.target.closest("a");
    if (link) return;

    // Only left-click should open the modal (allow middle/right clicks to do nothing).
    if (e.button !== 0) return;

    // Respect modifier keys (cmd/ctrl/alt/shift) so users can open in new tabs, etc.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    e.preventDefault();
    e.stopPropagation();

    const cell = e.currentTarget;
    const date = cell.dataset.calendarActionsDateValue;
    this.currentCell = cell;
    this.currentDate = date;

    // Date text
    this.dateTextTarget.textContent = this.pretty(date);

    if (this.hasMissedDateInputTarget) {
      this.missedDateInputTarget.value = this.currentDate; // e.g., "2025-09-10"
    }

    if (this.hasMissedDayFormTarget && (!this.missedDayFormTarget.action || this.missedDayFormTarget.action.endsWith("#"))) {
      const action = this.hasMissedDayPathValue && this.missedDayPathValue
        ? this.missedDayPathValue
        : (this.hasRtpIdValue ? `/rtps/${this.rtpIdValue}/missed_days` : null);

      if (action) this.missedDayFormTarget.action = action;
    }

    // Conditionally show "New IR" button
    // if (this.hasIrNewBtnTarget) {
    //   if (this.hasShowNewIrButtonValue && this.showNewIrButtonValue === true) {
    //     this.irNewBtnTarget.classList.remove("d-none");
    //   } else {
    //     this.rtpNewBtnTarget.classList.add("d-none");
    //   }
    // }

    if (this.hasIrFieldBtnTarget && this.hasIrClinicalBtnTarget) {
      if (this.hasShowNewIrButtonValue && this.showNewIrButtonValue === true) {
        this.irFieldBtnTarget.classList.remove("d-none");
        this.irClinicalBtnTarget.classList.remove("d-none");
      } else {
        this.irFieldBtnTarget.classList.add("d-none");
        this.irClinicalBtnTarget.classList.add("d-none");
      }
    }

    // Conditionally show "New RTP" button
    if (this.hasRtpNewBtnTarget) {
      if (this.hasShowNewRtpButtonValue && this.showNewRtpButtonValue === true) {
        this.rtpNewBtnTarget.classList.remove("d-none");
      } else {
        this.rtpNewBtnTarget.classList.add("d-none");
      }
    }

    // Conditionally show "Missed Days" buttons
    if (this.hasMissedDayWrapTarget) {
      if (this.hasShowMissedDaysButtonValue && this.showMissedDaysButtonValue === true) {
        this.missedDayWrapTarget.classList.remove("d-none");
      } else {
        this.missedDayWrapTarget.classList.add("d-none");
      }
    }

    // Conditionally show "New RTP Phase" button
    if (this.hasPhaseNewBtnTarget) {
      if (this.hasShowNewRtpPhaseButtonValue && this.showNewRtpPhaseButtonValue === true) {
        this.phaseNewBtnTarget.classList.remove("d-none");
        this.phaseOtherBtnTarget.classList.remove("d-none");
      } else {
        this.phaseNewBtnTarget.classList.add("d-none");
        this.phaseOtherBtnTarget.classList.add("d-none");
      }
    }

    // Show & position
    this.overlayTarget.classList.remove("d-none");
    document.documentElement.style.overflow = "hidden";
    this.sheetTarget.style.pointerEvents = "none";
    this.sheetTarget.style.opacity = "0";
    this.sheetTarget.style.transform = "none";

    requestAnimationFrame(() => {
      this.positionSheetOver(cell);
      this.sheetTarget.style.pointerEvents = "auto";
      this.sheetTarget.style.opacity = "1";
      this.sheetTarget.focus({ preventScroll: true });
    });
  }

  close() {
    this.overlayTarget.classList.add("d-none")
    document.documentElement.style.overflow = ""
    this.currentCell = null
    this.currentDate = null
  }

  pretty(iso) {
    return new Date(`${iso}T12:00`).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric"
    })
  }

  stop(e) {
    e.stopPropagation();
  }

  positionSheetOver(cell) {
    const r = cell.getBoundingClientRect()
    const sr = this.sheetTarget.getBoundingClientRect()

    const targetX = r.left + r.width / 2
    const targetY = r.top + r.height / 2

    let left = targetX - sr.width / 2
    let top  = targetY - sr.height / 2

    left = Math.max(12, Math.min(left, window.innerWidth  - sr.width  - 12))
    top  = Math.max(12, Math.min(top,  window.innerHeight - sr.height - 12))

    this.sheetTarget.style.left = `${left}px`
    this.sheetTarget.style.top  = `${top}px`
  }

  navigateTo(url) { if (url) window.location.href = url }

  phaseNewUrl(date) {
    const base = this.phaseNewPathValue
    if (!base) return null
    const url = new URL(base, window.location.origin)
    url.searchParams.set("effective_date", date)
    if (this.hasAffiliateIdValue) url.searchParams.set("affiliate_id", this.affiliateIdValue)
    if (this.hasAthleteIdValue)   url.searchParams.set("athlete_id", this.athleteIdValue)
    return url.toString()
  }

  showRtpUrl() {
    const base = this.rtpShowPathValue
    if (!base) return null
    const url = new URL(base, window.location.origin)
    return url.toString()
  }

  rtpNewUrl(date) {
    const base = this.rtpNewPathValue
    if (!base) return null
    const url = new URL(base, window.location.origin)
    if (this.hasAthleteIdValue)   url.searchParams.set("athlete_id", this.athleteIdValue)
    if (this.hasAffiliateIdValue) url.searchParams.set("affiliate_id", this.affiliateIdValue)
    if (this.hasLatestIrIdValue)  url.searchParams.set("typed_incident_report_id", this.latestIrIdValue)
    if (date) url.searchParams.set("effective_date", date)
    return url.toString()
  }

  irNewUrl(date) {
    const base = this.irNewPathValue
    if (!base) return null
    const url = new URL(base, window.location.origin)
    url.searchParams.set("occurred_on", date)
    if (this.hasAthleteProfileIdValue)  url.searchParams.set("athlete_profile_id", this.athleteProfileIdValue)
    if (this.hasAthleteIdValue)   url.searchParams.set("athlete_id", this.athleteIdValue)
    if (this.hasAffiliateIdValue) url.searchParams.set("affiliate_id", this.affiliateIdValue)
    return url.toString()
  }

  newIrField() {
    this.visitIrWithType("field")
  }

  newIrClinical() {
    this.visitIrWithType("clinic")
  }

  // === Helpers ===
  visitIrWithType(type) {
    // Build params; adjust keys if your controller expects different names
    const params = {
      type: type,                                    // <-- required param
      affiliate_id: this.affiliateIdValue,           // helpful if your route needs it
      athlete_id: this.athleteIdValue,               // helpful for preselecting athlete
      athlete_profile_id: this.athleteProfileIdValue,               // helpful for preselecting athlete
      effective_date: this.effectiveDateParam()      // YYYY-MM-DD
    }

    const url = this.buildUrl(this.irNewPathValue, params)
    window.location.href = url
  }

  effectiveDateParam() {
    // Prefer a clean YYYY-MM-DD (avoids time zone surprises server-side)
    // Falls back to today if no value is provided
    const iso = this.hasDateValue && this.dateValue ? this.dateValue : new Date().toISOString()
    return iso.slice(0, 10)
  }

  buildUrl(base, params) {
    const u = new URL(base, window.location.origin)
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") u.searchParams.set(k, v)
    })
    return u.toString()
  }

  async saveMissedDay(date, value) {
    const endpoint = this.missedDayPathValue || "/missed_days"
    const body = {
      date,
      value,
      athlete_id:   this.hasAthleteIdValue   ? this.athleteIdValue   : null,
      affiliate_id: this.hasAffiliateIdValue ? this.affiliateIdValue : null
    }
    await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "text/vnd.turbo-stream.html, text/html, application/json"
      },
      body: JSON.stringify(body),
      credentials: "same-origin"
    })
    this.close()
  }

  afterSubmit(e) {
    if (e.detail?.success) {
      this.close()
      window.Turbo.visit(window.location.href, { action: "replace" }) // soft refresh calendar
    } else {
        // Optional: surface server error for debugging
        const resp = e.detail?.fetchResponse;
        resp?.text?.().then(txt => {
          console.error("Missed day save failed:", txt);
          alert(`Failed to save missed day${txt ? `: ${txt}` : ""}`);
        });
    }
  }

}
